import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { useGetCurrentMenuItem, useGetSubmenuItems } from '@nx-frontend/shared/hooks/useMenu';
import { $menuState, searchMenuItemByIdFx } from '@nx-frontend/shared/store/feature-menu-tree';
import {
  isProcessMenuItemType,
  MenuItem,
  MenuType,
  PortalProcessData,
  PortalProcessItem,
  PortalProcessItemStatus,
  PortalProcessMenuItem,
} from '@nx-frontend/shared/types/menu';
import { calcProcessData } from '@nx-frontend/shared/utils';

interface GetProcessDataProps {
  relatedMenuItemId: string;
  menuTypes?: MenuType[];
}

interface ProcessDataResult {
  menuType: MenuType;
  processItem: PortalProcessMenuItem;
  stepItems: MenuItem[];
  process: PortalProcessData;
  currentMenuItem: MenuItem | undefined;
  currentProcessItem: PortalProcessItem | undefined;
}

export const useGetProcessData = ({
  relatedMenuItemId,
  menuTypes = [MenuType.PORTAL_SIDEBAR, MenuType.PORTAL_TOPBAR, MenuType.PORTAL_FOOTER],
}: GetProcessDataProps): ProcessDataResult | undefined => {
  const [menuType, setMenuType] = useState<MenuType | undefined>(undefined);
  const [processItem, setProcessItem] = useState<PortalProcessMenuItem | undefined>(undefined);
  const [searchMenuItemById, menuState] = useUnit([searchMenuItemByIdFx, $menuState]);

  const current = useGetCurrentMenuItem();

  useEffect(() => {
    const findProcessMenuItem = async () => {
      const item = await searchMenuItemById({
        menuTypes,
        id: relatedMenuItemId,
      });
      if (item != null && isProcessMenuItemType(item.item)) {
        setProcessItem(item.item);
        setMenuType(item.menuType);
      }
    };
    findProcessMenuItem();
  }, [searchMenuItemById, menuState, menuTypes, relatedMenuItemId, menuType]);

  const stepItems = useGetSubmenuItems({ menuItemId: processItem?.id }); // .filter((item) => item?.visible !== false);

  if (processItem == null || menuType == null) {
    return undefined;
  }

  const itemsObject = processItem.process.items.reduce((acc, item) => {
    acc[item.id] = item.status;
    return acc;
  }, {} as { [key: string]: PortalProcessItemStatus });
  const items: PortalProcessItem[] = stepItems.map((item) => ({
    id: item.id,
    visible: item.visible,
    status: itemsObject[item.id] ?? 'todo',
  }));

  return {
    menuType,
    processItem,
    stepItems,
    process: calcProcessData({
      items,
      status: processItem.process.status,
      sequential: processItem.process.sequential,
    }),
    currentMenuItem: current?.item,
    currentProcessItem: current ? items.find(({ id }) => id === current.item.id) : undefined,
  };
};
